import { ApplicationRef, Injectable, PLATFORM_ID, inject, OnDestroy } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { Router, NavigationEnd } from '@angular/router';
import { filter, interval, fromEvent, throttleTime, take } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../environments/environment';
import { WINDOW } from './window.token';

@Injectable({
  providedIn: 'root'
})
export class SwUpdateService implements OnDestroy {
  private swUpdate = inject(SwUpdate);
  private platformId = inject(PLATFORM_ID);
  private router = inject(Router);
  private appRef = inject(ApplicationRef);
  private win = inject(WINDOW);
  private lastCheckTime = 0;
  private readonly MIN_CHECK_INTERVAL = 1 * 60 * 1000; // 1 minuto entre verificações
  private readonly VERSION_STORAGE_KEY = 'wosi_app_version';
  private readonly VERSION_CHECK_INTERVAL = 2 * 60 * 1000; // Verifica versão a cada 2 minutos
  private versionCheckInterval: any;

  constructor() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (!this.swUpdate.isEnabled) {
      console.warn('[SW Update] ⚠️ Service Worker não está habilitado');
      return;
    }

    console.log('[SW Update] ✅ Service Worker habilitado');

    // Inicializa a versão atual no localStorage e exibe a versão
    this.initializeVersion();

    this.checkForUpdates();
    this.handleUpdates();
    this.setupEventListeners();
    this.startVersionCheck(); // Inicia verificação manual da versão
  }

  private canCheckNgswVersion(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }
    return typeof navigator === 'undefined' || navigator.onLine !== false;
  }

  /**
   * Lê ngsw.json com cache-busting. Falhas de rede são esperadas (offline, extensões).
   */
  private fetchNgswJson(): Promise<{ appData?: { version?: string } } | null> {
    if (!this.canCheckNgswVersion()) {
      return Promise.resolve(null);
    }

    return fetch('/ngsw.json?ngsw-cache-bust=' + Date.now())
      .then((response) => {
        if (!response.ok) {
          return null;
        }
        return response.json();
      })
      .catch(() => null);
  }

  /**
   * Inicializa a versão atual no localStorage
   */
  private initializeVersion(): void {
    void this.fetchNgswJson().then((data) => {
      if (!data) {
        return;
      }

      const currentVersion = data.appData?.version || 'unknown';

      // Exibe a versão atual do sistema
      console.log('[SW Update] 📦 Versão atual do sistema:', currentVersion);

      const storedVersion = this.safeGetItem(this.VERSION_STORAGE_KEY);

      // Se não há versão armazenada ou é diferente, atualiza
      if (!storedVersion || storedVersion !== currentVersion) {
        if (storedVersion && storedVersion !== currentVersion) {
          console.log('[SW Update] 🎉 Nova versão detectada! Recarregando...');
          this.safeSetItem(this.VERSION_STORAGE_KEY, currentVersion);
          this.scheduleReloadWhenStable(500);
        } else {
          this.safeSetItem(this.VERSION_STORAGE_KEY, currentVersion);
        }
      }
    });
  }

  /**
   * Inicia verificação periódica da versão via ngsw.json
   * Funciona como fallback caso o Service Worker não responda
   */
  private startVersionCheck(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // Verifica imediatamente após um delay inicial
    setTimeout(() => {
      this.checkVersionManually();
    }, 5000);

    // Verifica periodicamente
    this.versionCheckInterval = setInterval(() => {
      this.checkVersionManually();
    }, this.VERSION_CHECK_INTERVAL);
  }

  /**
   * Verifica manualmente a versão no servidor comparando com localStorage
   */
  private checkVersionManually(): void {
    const storedVersion = this.safeGetItem(this.VERSION_STORAGE_KEY);

    void this.fetchNgswJson().then((data) => {
      if (!data) {
        return;
      }

      const serverVersion = data.appData?.version;

      if (!serverVersion) {
        return;
      }

      if (!storedVersion) {
        this.safeSetItem(this.VERSION_STORAGE_KEY, serverVersion);
        return;
      }

      if (storedVersion !== serverVersion) {
        console.log('[SW Update] 🎉 Nova versão detectada! Recarregando...');
        this.safeSetItem(this.VERSION_STORAGE_KEY, serverVersion);
        this.scheduleReloadWhenStable(1000);
      }
    });
  }

  private safeGetItem(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private safeSetItem(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch {
      // SecurityError / quota: não derruba a checagem de versão
    }
  }

  /**
   * Verifica atualizações em múltiplos momentos:
   * - Ao iniciar a aplicação
   * - Periodicamente a cada 1 hora
   */
  private checkForUpdates(): void {
    this.lastCheckTime = Date.now();

    this.swUpdate.checkForUpdate()
      .then(() => {
        this.lastCheckTime = Date.now();
      })
      .catch(error => {
        console.error('[SW Update] ❌ Erro na verificação:', error);
        this.lastCheckTime = Date.now();
      });

    // Verifica periodicamente a cada 1 hora
    interval(60 * 60 * 1000).subscribe(() => {
      this.checkForUpdateSafely();
    });
  }

  /**
   * Verifica atualização apenas se passou tempo suficiente desde a última verificação
   * Evita verificações excessivas e sobrecarga
   */
  private checkForUpdateSafely(): void {
    const now = Date.now();

    if (this.lastCheckTime === 0) {
      this.lastCheckTime = now;
      this.swUpdate.checkForUpdate()
        .then(() => {
          this.lastCheckTime = Date.now();
        })
        .catch(error => {
          console.error('[SW Update] ❌ Erro ao verificar atualização:', error);
          this.lastCheckTime = Date.now();
        });
      return;
    }

    const timeSinceLastCheck = now - this.lastCheckTime;

    if (timeSinceLastCheck >= this.MIN_CHECK_INTERVAL) {
      this.swUpdate.checkForUpdate()
        .then(() => {
          this.lastCheckTime = Date.now();
        })
        .catch(error => {
          console.error('[SW Update] ❌ Erro ao verificar atualização:', error);
          this.lastCheckTime = Date.now();
        });
    }
  }


  /**
   * Configura listeners para verificar atualizações em eventos específicos
   * Com throttle/debounce para evitar sobrecarga
   */
  private setupEventListeners(): void {
    fromEvent(window, 'focus')
      .pipe(throttleTime(30 * 1000))
      .subscribe(() => {
        this.checkForUpdateSafely();
      });

    fromEvent(document, 'visibilitychange')
      .pipe(
        filter(() => !document.hidden),
        throttleTime(30 * 1000)
      )
      .subscribe(() => {
        this.checkForUpdateSafely();
      });

    fromEvent(window, 'online').subscribe(() => {
      this.checkForUpdateSafely();
    });

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        throttleTime(2 * 60 * 1000)
      )
      .subscribe(() => {
        this.checkForUpdateSafely();
      });
  }

  /**
   * Detecta e aplica atualizações automaticamente
   */
  private handleUpdates(): void {
    // Detecta quando uma nova versão está disponível e pronta
    this.swUpdate.versionUpdates
      .pipe(
        filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY')
      )
      .subscribe(() => {
        console.log('[SW Update] 🎉 Nova versão detectada! Aplicando atualização...');
        this.applyUpdate();
      });

    // Monitora erros do Service Worker
    const sw = isPlatformBrowser(this.platformId) ? navigator.serviceWorker : undefined;
    if (sw?.getRegistration) {
      sw.getRegistration().then(reg => {
        if (reg?.active) {
          reg.active.addEventListener('error', (event) => {
            console.error('[SW Update] ❌ Erro no Service Worker:', event.error);
          });
        }
      }).catch(() => {
        // Ignora erros silenciosamente
      });
    }
  }

  /**
   * Recarrega para pegar a versão já baixada (VERSION_READY).
   *
   * Safari / NX-UI-WEBAPP-ANGULAR-1JS: NÃO chamar `activateUpdate()` antes do reload.
   * O ngsw remapeia o client para a versão nova na hora; o JS antigo em memória
   * continua fazendo `import()` de lazy chunks da versão anterior (ex.: meus-seguros)
   * → 404 / "Importing a module script failed". O navigate do reload faz
   * `assignVersion` → latest no ngsw (sem janela de mismatch).
   */
  private applyUpdate(): void {
    this.scheduleReloadWhenStable(300);
  }

  /**
   * Recarrega após a zona estabilizar para não abortar `import()` de lazy chunks
   * (Safari iOS: "Importing a module script failed" durante reload precoce).
   * Usado pelos fallbacks de `appData.version` (sem activateUpdate no meio).
   */
  private scheduleReloadWhenStable(extraDelayMs: number): void {
    this.appRef.isStable
      .pipe(filter((stable) => stable), take(1))
      .subscribe(() => {
        setTimeout(() => {
          this.win?.location.reload();
        }, extraDelayMs);
      });
  }

  /**
   * Método público para verificar atualizações manualmente
   */
  checkForUpdate(): Promise<boolean> {
    return this.swUpdate.checkForUpdate()
      .catch(error => {
        console.error('[SW Update] ❌ Erro na verificação manual:', error);
        throw error;
      });
  }

  /**
   * Limpa recursos ao destruir o serviço
   */
  ngOnDestroy(): void {
    if (this.versionCheckInterval) {
      clearInterval(this.versionCheckInterval);
    }
  }
}
